/**
 * Elevation system: plans a closed height profile over the skeleton ring
 * (Σ deltas = 0 by construction: hills alternate climb → descent and always
 * return to ground). Climbs/descents are reserved as concrete window slots
 * with restricted piece domains; all reserved pieces are 1-cell-wide, so
 * reservations can never collide with anything.
 *
 * All planned base heights are multiples of 0.5 — the support-stack grid.
 */
import type { Rng } from "./rng.ts";
import type { Skeleton } from "./topology.ts";
import type { PieceRegistry } from "./registry.ts";
import { applyXform, windowXform } from "./geom.ts";

/** A reserved window: realization must place one of `pieceIds` here. */
export interface Reservation {
  start: number;
  len: number;
  /** Candidate piece ids (forward defs); style picks among them. */
  pieceIds: readonly string[];
  /** True → use the reversed variant (descent). */
  reversed: boolean;
}

export interface ElevationPlan {
  /** Base height (piece origin y) at each ring cell's entry. */
  baseY: number[];
  reservations: Reservation[];
  maxBaseY: number;
}

interface Run {
  start: number;
  len: number;
}

/**
 * One contiguous stretch of a climb/descent. `ids` are interchangeable
 * alternatives (same length and delta); multi-part options chain parts
 * back-to-back so every intermediate base stays on the 0.5 support grid.
 */
export interface SlopePart {
  ids: string[];
  len: number;
  delta: number;
  /** The FORWARD def already descends (drop-stacks, helixes) — reserve it
   * unreversed even inside a descent. */
  asIs?: boolean;
}

export interface SlopeOption {
  totalLen: number;
  parts: SlopePart[];
}

const slope = (...parts: SlopePart[]): SlopeOption => ({
  totalLen: parts.reduce((a, p) => a + p.len, 0),
  parts,
});

/**
 * Climbs cap at 0.5 rise per cell — a marble visibly ascending anything
 * steeper reads as broken. Taller climbs chain gentle parts instead.
 */
export const CLIMB_VOCAB: Record<string, SlopeOption[]> = {
  "0.5": [
    slope({ ids: ["slant-b"], len: 1, delta: 0.5 }),
    slope({ ids: ["ramp-long-b", "slant-long-b"], len: 2, delta: 0.5 }),
    slope(
      { ids: ["ramp-start-b"], len: 1, delta: 0.25 },
      { ids: ["ramp-end-b"], len: 1, delta: 0.25 },
    ),
  ],
  "1": [
    slope({ ids: ["ramp-long-c", "slant-long-c"], len: 2, delta: 1.0 }),
    slope(
      { ids: ["ramp-start-c"], len: 1, delta: 0.5 },
      { ids: ["ramp-end-c"], len: 1, delta: 0.5 },
    ),
  ],
  "1.5": [
    slope(
      { ids: ["ramp-start-c"], len: 1, delta: 0.5 },
      { ids: ["ramp-end-c"], len: 1, delta: 0.5 },
      { ids: ["slant-b"], len: 1, delta: 0.5 },
    ),
    slope(
      { ids: ["ramp-long-b", "slant-long-b"], len: 2, delta: 0.5 },
      { ids: ["ramp-long-c", "slant-long-c"], len: 2, delta: 1.0 },
    ),
  ],
  // Only the full-helix ensemble asks for 2.0 (its mandatory climb).
  "2": [
    slope(
      { ids: ["ramp-long-c", "slant-long-c"], len: 2, delta: 1.0 },
      { ids: ["ramp-long-c", "slant-long-c"], len: 2, delta: 1.0 },
    ),
    slope(
      { ids: ["ramp-start-c"], len: 1, delta: 0.5 },
      { ids: ["ramp-end-c"], len: 1, delta: 0.5 },
      { ids: ["ramp-long-c", "slant-long-c"], len: 2, delta: 1.0 },
    ),
  ],
};

/** Descents also allow steeper chute drops (0.75 per cell) and drop-stacks
 * (end-hole over a receiver end: the marble falls through the hole — the
 * channel network disconnects there but the loop closes through the fall). */
export const DESCENT_VOCAB: Record<string, SlopeOption[]> = {
  "0.5": [
    ...CLIMB_VOCAB["0.5"],
    slope({
      ids: ["drop-stack-rounded-f", "drop-stack-square-f"],
      len: 1,
      delta: 0.5,
      asIs: true,
    }),
  ],
  "1": CLIMB_VOCAB["1"],
  "1.5": [
    ...CLIMB_VOCAB["1.5"],
    slope({ ids: ["slant-long-d", "ramp-long-d"], len: 2, delta: 1.5 }),
    slope({ ids: ["slant-long-d", "ramp-long-d"], len: 2, delta: 1.5 }),
    // Chute pair: 0.75/cell — exactly the descent steepness cap.
    slope(
      { ids: ["ramp-start-d"], len: 1, delta: 0.75 },
      { ids: ["ramp-end-d"], len: 1, delta: 0.75 },
    ),
  ],
};

/**
 * Corner drops: a hill leg may ride a helix at a turn window instead of a
 * ramp on a straight run. Forward helix defs are descents (deckDelta < 0);
 * a climb through the same window pattern is the REVERSED variant of the
 * opposite-chirality helix (reversing swaps L↔R in the moves). The window
 * always covers the hill's full delta, so the helix's integral base sits on
 * the ground at its low end (hills start/end at base 0).
 */
export const TURN_DESCENTS: Record<string, { delta: number; ids: string[] }> = {
  // Bare corners can host a drop-stack: end-hole over a receiver end whose
  // port exits through the turn. Guaranteed fall; len²-weighting keeps real
  // helix windows preferred when both exist.
  L: { delta: 0.5, ids: ["drop-stack-rounded-l", "drop-stack-square-l"] },
  R: { delta: 0.5, ids: ["drop-stack-rounded-r", "drop-stack-square-r"] },
  FLF: { delta: 0.5, ids: ["helix-quarter-left"] },
  FRF: { delta: 0.5, ids: ["helix-quarter-right"] },
  FFLFF: { delta: 0.5, ids: ["helix-large-quarter-left"] },
  FFRFF: { delta: 0.5, ids: ["helix-large-quarter-right"] },
  FLFFLF: { delta: 1.0, ids: ["helix-half-left"] },
  FRFFRF: { delta: 1.0, ids: ["helix-half-right"] },
  FFLFFFFLFF: { delta: 1.0, ids: ["helix-large-half-left"] },
  FFRFFFFRFF: { delta: 1.0, ids: ["helix-large-half-right"] },
};
const TURN_CLIMBS: Record<string, { delta: number; ids: string[] }> = {
  FLF: { delta: 0.5, ids: ["helix-quarter-right"] },
  FRF: { delta: 0.5, ids: ["helix-quarter-left"] },
  FFLFF: { delta: 0.5, ids: ["helix-large-quarter-right"] },
  FFRFF: { delta: 0.5, ids: ["helix-large-quarter-left"] },
  FLFFLF: { delta: 1.0, ids: ["helix-half-right"] },
  FRFFRF: { delta: 1.0, ids: ["helix-half-left"] },
  FFLFFFFLFF: { delta: 1.0, ids: ["helix-large-half-right"] },
  FFRFFFFRFF: { delta: 1.0, ids: ["helix-large-half-left"] },
};

/** How often the track's corner set-piece (a helix window) is attempted. */
const CORNER_DESCENT_AFFINITY = 0.5;
const CORNER_CLIMB_AFFINITY = 0.3;

interface TurnWindow {
  start: number;
  len: number;
  pattern: string;
  delta: number;
}

/**
 * All ring windows whose move pattern matches a helix def, unblocked, and
 * whose footprint extras don't collide with the rest of the ring (the world
 * cells a helix covers are the same for the descent and the climb variant,
 * so the forward def's footprint is checked once).
 */
function findTurnWindows(
  skeleton: Skeleton,
  blocked: ReadonlySet<number>,
  registry: PieceRegistry,
): TurnWindow[] {
  const n = skeleton.cells.length;
  const moves = skeleton.cells.map((c) => c.move).join("");
  const ringCells = new Set(skeleton.cells.map((c) => `${c.x},${c.z}`));
  const out: TurnWindow[] = [];
  for (const [pattern, spec] of Object.entries(TURN_DESCENTS)) {
    const def = registry.variants.find(
      (v) => v.id === spec.ids[0] && !v.reversed,
    );
    if (!def) continue;
    for (let start = 0; start < n; start++) {
      let ok = true;
      const windowSet = new Set<string>();
      for (let j = 0; j < pattern.length; j++) {
        const idx = (start + j) % n;
        if (blocked.has(idx) || moves[idx] !== pattern[j]) {
          ok = false;
          break;
        }
        const c = skeleton.cells[idx];
        windowSet.add(`${c.x},${c.z}`);
      }
      if (!ok) continue;
      const cell = skeleton.cells[start];
      const xf = windowXform(cell.x, cell.z, cell.dirIn, 0);
      for (const [fx, fz] of def.footprint) {
        const w = applyXform(xf, [fx, 0, fz]);
        const k = `${Math.round(w[0])},${Math.round(w[2])}`;
        if (!windowSet.has(k) && ringCells.has(k)) {
          ok = false;
          break;
        }
      }
      if (ok) {
        out.push({ start, len: pattern.length, pattern, delta: spec.delta });
      }
    }
  }
  return out.sort((a, b) => a.start - b.start || b.len - a.len);
}

export function planElevation(
  rng: Rng,
  skeleton: Skeleton,
  registry: PieceRegistry,
  /** Ring indices claimed by the feature planner — hills avoid them. */
  blocked: ReadonlySet<number> = new Set(),
): ElevationPlan {
  const n = skeleton.cells.length;
  const deltas = new Array<number>(n).fill(0);
  const reservations: Reservation[] = [];

  // A full-helix ensemble owns its whole window; its cells never host other
  // slopes, and its mandatory 2.0 climb is reserved before any hills.
  const fullBlocked = new Set(blocked);
  for (const loop of skeleton.helixLoops) {
    for (let j = 0; j < loop.len; j++) fullBlocked.add(loop.start + j);
  }

  // Maximal straight unblocked runs (skeleton is normalized to start at a
  // turn, so runs never wrap the seam).
  const runs: Run[] = [];
  let i = 0;
  while (i < n) {
    if (skeleton.cells[i].move !== "F" || fullBlocked.has(i)) {
      i++;
      continue;
    }
    let len = 0;
    while (
      i + len < n && skeleton.cells[i + len].move === "F" &&
      !fullBlocked.has(i + len)
    ) len++;
    runs.push({ start: i, len });
    i += len;
  }

  // Full-helix ensemble: reserve the spiral window (drop 2.0) and its
  // mandatory climb; the loop is then THE hill of the track.
  const helixLoop = skeleton.helixLoops[0];
  if (helixLoop) {
    reservations.push({
      start: helixLoop.start,
      len: helixLoop.len,
      pieceIds: [helixLoop.id],
      reversed: false,
    });
    deltas[helixLoop.start] -= 2;
    const climbRuns = runs.filter((r) => r.len >= 4);
    if (climbRuns.length === 0) {
      // The mutation pre-checked for a ≥4 run; features can't take it
      // (generateTrack skips wide runs on helix-loop tracks).
      throw new Error("helix loop without a climb run");
    }
    const longest = climbRuns.reduce((a, b) => (b.len > a.len ? b : a));
    reserve(rng.child("helix-climb"), longest, 2.0, false);
  }

  // Bridge pierce: the hairpin climbs +1.0 on its columns, passes OVER the
  // crossed run, and descends on the far column. The pierce splice fixes
  // the layout (columns at indices 1..k and k+1..2k, crossings at d and
  // 2k+1−d), so the climb sits below the near crossing and the descent
  // below the far one — both upper visits land at exactly 1.0.
  const bridge = skeleton.pierce?.bridge ? skeleton.pierce : null;
  if (bridge) {
    reserve(
      rng.child("bridge-climb"),
      { start: 1, len: bridge.d - 1 },
      1.0,
      false,
    );
    reserve(
      rng.child("bridge-descent"),
      { start: 2 * bridge.k - bridge.d + 2, len: bridge.d - 1 },
      1.0,
      true,
    );
  }

  // Corner drop, planned BEFORE hills grab their straight runs: the big
  // helix windows straddle several runs, so picking runs first starved them
  // (helix-large-half never appeared). One corner set-piece per track:
  // a length²-weighted window (big spirals win when present) reserved as
  // the descent, paired with a straight climb — or a second, disjoint
  // corner window as a spiral climb. Ring order is free: the baseY profile
  // is normalized to min 0 after integration, so descent-before-climb
  // simply means the elevated stretch wraps the seam.
  // Helix-loop tracks stay flat elsewhere (the spiral IS the hill). Grade
  // crossings no longer force a flat track (M13 lifts the M3 compromise):
  // plain hills are admitted under the HILL-INTERVAL CONSTRAINT below;
  // corner set-pieces stay off crossing tracks (multi-window ensembles
  // and the pierce splice both pin ring geometry the constraint can't
  // reason about).
  const flatOnly = Boolean(helixLoop);
  const crossingPairs = skeleton.crossings;
  const corner = rng.child("corner");
  const usedRuns = new Set<Run>();
  if (
    !flatOnly && crossingPairs.length === 0 &&
    corner.chance(CORNER_DESCENT_AFFINITY)
  ) {
    const turnWindows = findTurnWindows(skeleton, fullBlocked, registry);
    if (turnWindows.length > 0) {
      const pickTurn = (opts: TurnWindow[]): TurnWindow =>
        corner.weighted(opts.map((w) => [w, w.len * w.len] as const));
      const reserveTurn = (w: TurnWindow, descent: boolean) => {
        const spec = (descent ? TURN_DESCENTS : TURN_CLIMBS)[w.pattern];
        reservations.push({
          start: w.start,
          len: w.len,
          pieceIds: spec.ids,
          reversed: !descent,
        });
        spreadDelta(w.start, w.len, descent ? -w.delta : w.delta);
      };
      const descent = pickTurn(turnWindows);
      const windowIdx = new Set<number>();
      for (let j = 0; j < descent.len; j++) {
        windowIdx.add((descent.start + j) % n);
      }
      const overlapsWindow = (r: Run): boolean => {
        for (let j = 0; j < r.len; j++) {
          if (windowIdx.has(r.start + j)) return true;
        }
        return false;
      };
      const disjoint = (w: TurnWindow): boolean => {
        for (let j = 0; j < w.len; j++) {
          if (windowIdx.has((w.start + j) % n)) return false;
        }
        return true;
      };
      const climbWindows = turnWindows.filter((w) =>
        w !== descent && w.delta === descent.delta && disjoint(w) &&
        // Drop-stack windows (bare L/R) have no climb form — you can't
        // fall upward through a hole.
        TURN_CLIMBS[w.pattern] !== undefined
      );
      const climbRuns = runs.filter((r) =>
        !overlapsWindow(r) &&
        CLIMB_VOCAB[String(descent.delta)].some((o) => o.totalLen <= r.len)
      );
      if (climbWindows.length > 0 && corner.chance(CORNER_CLIMB_AFFINITY)) {
        reserveTurn(pickTurn(climbWindows), false);
        reserveTurn(descent, true);
      } else if (climbRuns.length > 0) {
        const run = corner.pick(climbRuns);
        reserve(rng.child("climb/corner"), run, descent.delta, false);
        reserveTurn(descent, true);
        usedRuns.add(run);
      }
      // No climb available → the corner drop is silently skipped.
    }
  }

  const cornerIdx = new Set<number>();
  for (const r of reservations) {
    for (let j = 0; j < r.len; j++) cornerIdx.add((r.start + j) % n);
  }
  // HILL-INTERVAL CONSTRAINT (M13, lifts the M3 "crossings stay flat"
  // compromise): both visits of a crossing pair must agree on their height
  // RELATIONSHIP — baseY[b] − baseY[a] is the sum of window deltas in the
  // ring interval (a, b], so a hill is safe iff its climb and descent land
  // on the SAME SIDE of every pair's interval (net contribution zero).
  // Runs touching a crossing cell are excluded outright (the cross piece
  // is one flat 4-way — a slope window may not cover it), and any run
  // free of the crossing cells lies entirely on one side of each interval.
  const crossingCells = new Set<number>(
    crossingPairs.flatMap((c) => [c.a, c.b]),
  );
  const eligible = runs.filter((r) => {
    if (usedRuns.has(r)) return false;
    for (let j = 0; j < r.len; j++) {
      if (cornerIdx.has(r.start + j)) return false;
      if (crossingCells.has((r.start + j) % n)) return false;
    }
    return r.len >= 2;
  });
  const sameSide = (r1: Run, r2: Run): boolean =>
    crossingPairs.every(({ a, b }) => {
      const s1 = r1.start > a && r1.start <= b;
      const s2 = r2.start > a && r2.start <= b;
      return s1 === s2;
    });
  const maxHills = Math.min(3, Math.floor(eligible.length / 2));
  // At least one hill whenever the ring can host one — flat tracks still
  // occur (small rings), but stay the exception. Helix-loop tracks: the
  // spiral IS the hill.
  const hills = maxHills > 0 && !flatOnly ? rng.int(1, maxHills + 1) : 0;

  if (hills > 0) {
    const chosen = rng.shuffle(eligible).slice(0, hills * 2)
      .sort((a, b) => a.start - b.start);
    for (let h = 0; h < hills; h++) {
      const climbRun = chosen[h * 2];
      const descentRun = chosen[h * 2 + 1];
      // A pair split by a crossing interval would tilt the pair's height
      // relationship — skip that hill (the pairing is seeded; another
      // seed pairs differently).
      if (!sameSide(climbRun, descentRun)) continue;
      // Only deltas both runs can physically host (0.5 always fits).
      const choices = ([[0.5, 3], [1.0, 2], [1.5, 2]] as const).filter(([d]) =>
        CLIMB_VOCAB[String(d)].some((o) => o.totalLen <= climbRun.len) &&
        DESCENT_VOCAB[String(d)].some((o) => o.totalLen <= descentRun.len)
      );
      const delta = rng.weighted(choices);
      reserve(rng.child(`climb/${h}`), climbRun, delta, false);
      reserve(rng.child(`descent/${h}`), descentRun, delta, true);
    }
  }

  function reserve(
    r: Rng,
    run: Run,
    delta: number,
    reversed: boolean,
  ): { start: number; len: number } {
    const vocab = reversed ? DESCENT_VOCAB : CLIMB_VOCAB;
    const usable = vocab[String(delta)].filter((o) => o.totalLen <= run.len);
    const option = r.pick(usable);
    const start = run.start + r.int(0, run.len - option.totalLen + 1);
    // Descents traverse the part sequence in reverse order.
    const parts = reversed ? option.parts.slice().reverse() : option.parts;
    let at = start;
    for (const part of parts) {
      reservations.push({
        start: at,
        len: part.len,
        pieceIds: part.ids,
        reversed: reversed && !part.asIs,
      });
      spreadDelta(at, part.len, reversed ? -part.delta : part.delta);
      at += part.len;
    }
    return { start, len: option.totalLen };
  }

  function spreadDelta(start: number, _len: number, delta: number) {
    // The whole window's delta lands at its start index; baseY integration
    // below treats a window as one step.
    deltas[start] += delta;
  }

  // Integrate deltas around the ring: baseY[i] is the entry base of cell i.
  const baseY = new Array<number>(n).fill(0);
  let hgt = 0;
  for (let idx = 0; idx < n; idx++) {
    baseY[idx] = hgt;
    hgt += deltas[idx];
  }
  if (Math.abs(hgt) > 1e-9) {
    throw new Error(`elevation did not close: residual ${hgt}`);
  }
  // Ground the profile: the integration constant is free on a closed loop,
  // so shift the lowest point to base 0. Hills built climb-before-descent
  // already sit at 0; the helix loop's drop-then-climb (in index order)
  // relies on this.
  const minY = Math.min(...baseY);
  if (minY !== 0) {
    for (let idx = 0; idx < n; idx++) baseY[idx] -= minY;
  }

  return { baseY, reservations, maxBaseY: Math.max(...baseY) };
}
